


































import {
  computed,
  ref,
  defineComponent,
  useStore,
  onMounted,
} from '@nuxtjs/composition-api'
import CategoryMenuItem from '@/components/Header/CategoryMenuItem.vue'
import PopupTooltip from '@/components/Popup/PopupTooltip.vue'
import CategoryMenuListItem from '@/components/Header/CategoryMenuListItem.vue'
import offscreenMeasure from '@/composition/offscreenMeasure'
import { useTrackScrollDistance } from '@/composition/useTrackScrollDistance'

const getOverflowVisibleIndex = (
  container: HTMLElement,
  additionalElementsWidth: number,
  allowedInitialWidth: number
) => {
  const styles = window.getComputedStyle(container)
  const allowedWidth =
    allowedInitialWidth -
    (parseInt(styles.paddingLeft) + parseInt(styles.paddingRight))
  let calculatedWidth = 0
  let overflowIndex = -1

  const items = Array.from<HTMLElement>(container.children).map(
    (e) => e.offsetWidth
  )
  for (let i = 0; i < items.length; i += 1) {
    calculatedWidth += items[i]
    if (calculatedWidth > allowedWidth) {
      overflowIndex = i
      break
    }
  }
  if (overflowIndex === -1) return items.length
  for (let i = overflowIndex; i >= 0; i -= 1) {
    if (calculatedWidth <= 0) return -1
    if (calculatedWidth + additionalElementsWidth <= allowedWidth) return i
    calculatedWidth -= items[i]
  }

  return -1
}

const HEADER_SIDE_MARGIN_DESK = 2.4
const HEADER_SIDE_MARGIN_MOB = 1.6

export default defineComponent({
  name: 'SectionCategoriesMenu',
  components: {
    CategoryMenuListItem,
    PopupTooltip,
    CategoryMenuItem,
  },
  setup() {
    const isMobile = computed(() => store.getters['mediaQuery/isMobileMq'])
    const allowedHeaderInitialWidth = ref(0)
    const store = useStore()
    const menu = computed(() => store.getters['settings/getCategoriesMenu'])
    const allItems = computed(() => {
      const items = menu.value?.items || []
      return isMobile.value ? items.filter((item) => !item.mobileHidden) : items
    })
    const categoriesPopupVisible = ref<boolean>(false)
    const containerElement = ref<HTMLDivElement>(null)

    const additionalMenuItem = computed(() => ({
      id: 'categories-menu-trigger',
      url: '',
      title: menu.value?.collapsedItemsButtonText || 'Другие',
      iconSvg: menu.value?.collapsedItemsButtonIcon,
      internalIcon: 'icon/categories-menu-more',
    }))

    const preparedItems = ref(null)

    const items = computed(() => {
      if (preparedItems.value) return preparedItems.value
      return {
        all: allItems.value.slice(),
        visible: allItems.value.slice(),
        collapsed: [],
      }
    })

    const onClick = (e) => {
      if (e.id === 'categories-menu-trigger') {
        categoriesPopupVisible.value = !categoriesPopupVisible.value
      }
    }

    const openPopup = () => {
      categoriesPopupVisible.value = true
    }
    const closePopup = () => {
      categoriesPopupVisible.value = false
    }

    useTrackScrollDistance(closePopup)

    const calculateLayout = () => {
      if (!containerElement.value) return
      const allowedWidth =
        allowedHeaderInitialWidth.value || containerElement.value.clientWidth
      const isOverflowed = containerElement.value.scrollWidth > allowedWidth
      if (isOverflowed) {
        const size = offscreenMeasure(CategoryMenuItem, {
          item: additionalMenuItem.value,
        })
        const index = getOverflowVisibleIndex(
          containerElement.value,
          parseInt(size.width),
          allowedWidth
        )
        const all = allItems.value.slice()
        const visible = all.slice(0, index + 1)
        const collapsed = all.slice(index + 1)
        visible.push(additionalMenuItem.value)
        preparedItems.value = { all, visible, collapsed }
      }
    }

    onMounted(() => {
      // Хедер может быть изначально растянут на всю ширину, но свободное место необходимо вычислить в состоянии, когда хедер не растянут
      // Отсюда костыль с вычислением гипотетических размеров хедера. Альтернатива - рендерить свернутый хедер в невидимую область,
      // что неоправданно усложняет реализацию
      const documentStyles = getComputedStyle(document.documentElement)
      const headerPaddings = isMobile.value
        ? HEADER_SIDE_MARGIN_MOB
        : HEADER_SIDE_MARGIN_DESK
      const sidePaddings = headerPaddings * parseFloat(documentStyles.fontSize)
      allowedHeaderInitialWidth.value = Math.floor(
        document.body.clientWidth - 2 * sidePaddings
      )
      calculateLayout()
    })
    return {
      menu,
      items,
      onClick,
      containerElement,
      categoriesPopupVisible,
      openPopup,
      closePopup,
    }
  },
})
